import { useApi, useLoad } from "@allergy123/api-client/react";
import { KNOWN_PERMISSIONS, type OrganizationMemberSummary, type Permission } from "@allergy123/contracts";
import { Button, Card, Select, Status, Table, Td, Th } from "@allergy123/ui";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { memberAction, memberActionLabel } from "./access";
import { errorText } from "./catalog/common";

type MembershipStatus = OrganizationMemberSummary["status"];

interface Draft {
  permissions: string[];
  locationIds: string[];
  status: MembershipStatus;
}

/** The three states a membership can be put in from here; only an active one grants anything. */
const STATUS_OPTIONS: { value: MembershipStatus; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "ended", label: "Ended" },
];
const STATUS_HINTS: Record<MembershipStatus, string> = {
  active: "Has the practice's access with the permissions below.",
  suspended: "Keeps the permissions but none of the practice's access until reinstated.",
  ended: "Loses the practice's access and permissions. The sign-in itself is not disabled here.",
};

/** How each permission reads to staff (api-v2.md section 4.1). */
const PERMISSION_LABELS: Record<Permission, string> = {
  "clinical:read": "Clinical: read",
  "clinical:write": "Clinical: write",
  "clinical:order": "Clinical: order",
  "clinical:manage": "Clinical: manage",
  "learning:assign": "Learning: assign",
};

const sameSet = (a: string[], b: string[]) => a.length === b.length && [...a].sort().join("\u0000") === [...b].sort().join("\u0000");
const sameDraft = (d: Draft, m: OrganizationMemberSummary) => sameSet(d.permissions, m.permissions) && sameSet(d.locationIds, m.locationIds) && d.status === m.status;

/** Keyed by id, so a different organization never shows the last one's unsaved choices or messages. */
export function OrganizationMembersPage() {
  const { id = "" } = useParams();
  return <OrganizationMembersEditor key={id} id={id} />;
}

/**
 * Each member's status, clinical and learning permissions, and location scope.
 * The organization also needs clinical access (granted on Organizations)
 * before any clinical permission takes effect; the API checks both. Suspending
 * or ending a membership takes the practice's access away on the member's next
 * request; it does not disable their sign-in (that is the lost-device runbook).
 */
function OrganizationMembersEditor({ id }: { id: string }) {
  const api = useApi();
  const members = useLoad((a) => a.admin.members(id));
  const organizations = useLoad((a) => a.admin.organizations());
  // Only members the person has changed; the rest read straight from the loaded list.
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [msg, setMsg] = useState<{ kind: "info" | "error"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const organization = organizations.data?.organizations.find((o) => o.id === id);
  const hasClinical = organization?.products.includes("clinical") ?? true;

  // Once a reload shows a member with the choices that were saved, they no longer need their own entry.
  useEffect(() => {
    setDrafts((ds) => {
      const next = { ...ds };
      for (const m of members.data?.members ?? []) {
        const d = next[m.userId];
        if (d && sameDraft(d, m)) delete next[m.userId];
      }
      return next;
    });
  }, [members.data]);

  const draftOf = (m: OrganizationMemberSummary): Draft => drafts[m.userId] ?? { permissions: m.permissions, locationIds: m.locationIds, status: m.status };
  const label = (m: OrganizationMemberSummary) => m.name ?? m.email;

  async function save(m: OrganizationMemberSummary) {
    const d = drafts[m.userId];
    if (busy || !d) return;
    setBusy(true);
    setMsg(null);
    try {
      // Only the known permissions are sent; anything else on the membership is dropped on purpose.
      const permissions = KNOWN_PERMISSIONS.filter((p) => d.permissions.includes(p));
      // The status rides along only when it changed, so a permissions save reads as one in Audit.
      await api.admin.updateMember(id, m.userId, { permissions, locationIds: d.locationIds, ...(d.status !== m.status ? { status: d.status } : {}) });
      setMsg({ kind: "info", text: d.status !== m.status ? `Saved ${label(m)}: ${d.status === "active" ? "reinstated" : d.status}.` : `Saved ${label(m)}.` });
      members.reload();
    } catch (err) {
      setMsg({ kind: "error", text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }

  const locationOptions = (members.data?.locations ?? []).map((l) => ({ value: l.id, label: l.name }));
  const count = members.data?.members.length;
  return (
    <>
      <div className="mb-8">
        <nav aria-label="Breadcrumb" className="mb-4 text-small">
          <Link to="/organizations" className="text-anchor underline">Organizations</Link>
          {(organization ?? members.data?.organization) && <span className="text-ink-secondary"> / {(organization ?? members.data?.organization)!.name}</span>}
        </nav>
        <h1 className="mb-2 text-h1 font-semibold text-anchor">Members</h1>
        <p className="max-w-[68ch] text-ink-secondary">Who belongs to this practice, what each person may do, and at which locations. Leave the locations empty for every location. Suspend or end a membership to take the practice's access away; it applies on the person's next request. Every change here is audited.</p>
      </div>
      {members.state === "error" && <div className="mb-6"><Status kind="error" label={`Couldn't load members (${errorText(members.error)})`} /></div>}
      {organizations.state === "error" && <div className="mb-6"><Status kind="error" label={`Couldn't load the organization's products (${errorText(organizations.error)})`} /></div>}
      {!hasClinical && <div className="mb-6"><Status kind="warning" label="This organization has no clinical access yet, so clinical permissions do nothing until it is granted on Organizations." /></div>}
      {msg && <div className="mb-6"><Status kind={msg.kind} label={msg.text} /></div>}
      <Card>
        <Table caption={count !== undefined ? `${count} member${count === 1 ? "" : "s"}` : members.state === "error" ? "Not loaded" : "Loading…"}>
          <thead>
            <tr>
              <Th>Member</Th>
              <Th>Role</Th>
              <Th>Status</Th>
              <Th>Permissions</Th>
              <Th>Locations</Th>
              <Th><span className="sr-only">Actions</span></Th>
            </tr>
          </thead>
          <tbody>
            {members.data?.members.map((m) => {
              const d = draftOf(m);
              const changed = !sameDraft(d, m);
              const setDraft = (patch: Partial<Draft>) => setDrafts({ ...drafts, [m.userId]: { ...d, ...patch } });
              // Ending clears what the membership held; suspending keeps it for reinstatement. Choosing another
              // status after Ended brings back what was saved, so a slip on the select loses nothing.
              const setStatus = (status: MembershipStatus) =>
                setDraft(status === "ended" ? { status, permissions: [], locationIds: [] } : d.status === "ended" ? { status, permissions: m.permissions, locationIds: m.locationIds } : { status });
              const editable = d.status === "active";
              return (
                <tr key={m.userId}>
                  <Td>
                    <div className="font-medium">{m.name ?? <span className="text-ink-secondary">No name</span>}</div>
                    <div className="text-small text-ink-secondary">{m.email}</div>
                  </Td>
                  <Td>{m.role}</Td>
                  <Td>
                    <Select label={`Status for ${label(m)}`} value={d.status} onChange={(e) => setStatus(e.target.value as MembershipStatus)} options={STATUS_OPTIONS} hint={d.status !== m.status ? STATUS_HINTS[d.status] : undefined} className="min-w-[10rem]" />
                  </Td>
                  <Td>
                    <fieldset className="grid gap-1">
                      <legend className="sr-only">Permissions for {label(m)}</legend>
                      {KNOWN_PERMISSIONS.map((p) => (
                        <label key={p} className="flex items-center gap-2 text-small">
                          <input
                            type="checkbox"
                            checked={d.permissions.includes(p)}
                            disabled={!editable}
                            onChange={(e) => setDraft({ permissions: e.target.checked ? [...d.permissions, p] : d.permissions.filter((x) => x !== p) })}
                          />
                          {PERMISSION_LABELS[p]}
                        </label>
                      ))}
                    </fieldset>
                  </Td>
                  <Td>
                    <Select
                      label={`Locations for ${label(m)}`}
                      multiple
                      value={d.locationIds}
                      disabled={!editable || locationOptions.length === 0}
                      onChange={(e) => setDraft({ locationIds: Array.from(e.target.selectedOptions, (o) => o.value) })}
                      options={locationOptions}
                      hint={locationOptions.length === 0 ? "No locations yet." : d.locationIds.length === 0 ? "All locations." : undefined}
                    />
                  </Td>
                  <Td>
                    <Button variant="secondary" surface="card" disabled={busy || !changed} aria-label={memberActionLabel(m.status, d.status, label(m))} onClick={() => void save(m)}>
                      {memberAction(m.status, d.status)}
                    </Button>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        {count === 0 && <p className="mt-3 text-ink-secondary">This organization has no members yet. Invite the first one on Invite.</p>}
      </Card>
    </>
  );
}
